import { useState } from 'react';
import { 
  Zap, 
  Plus,
  Trash2,
  Calculator,
  AlertCircle,
  CheckCircle2,
  Info,
  Download,
  Database,
  Battery,
  Activity,
  BookOpen,
  Cable
} from 'lucide-react';
import { useProjects } from '../contexts/ProjectContext';
import { deviceDatabase } from '../data/deviceDatabase';
import { Link } from 'react-router-dom';

interface Device {
  id: string;
  name: string;
  type: 'smoke_detector' | 'heat_detector' | 'pull_station' | 'horn_strobe' | 'speaker' | 'module' | 'relay';
  quantity: number;
  standbyCurrentMa: number;
  alarmCurrentMa: number;
}

interface CircuitResult {
  totalStandbyCurrent: number;
  totalAlarmCurrent: number;
  requiredWireGauge: string;
  voltageDrop: number;
  voltageAtEOL: number;
  maxCircuitLength: number;
  powerSupplyRequired: number;
  batteryCapacity24h: number;
  batteryCapacity24h5m: number;
  chargerRating: number;
  status: 'success' | 'warning' | 'error';
  messages: string[];
  nfpaReferences: string[];
}

const devicePresets: Record<string, { standby: number; alarm: number }> = {
  'Smoke Detector (Photoelectric)': { standby: 0.05, alarm: 0.05 },
  'Smoke Detector (Ionization)': { standby: 0.08, alarm: 0.08 },
  'Heat Detector': { standby: 0.04, alarm: 0.04 },
  'Pull Station': { standby: 0.001, alarm: 0.001 },
  'Horn/Strobe (15cd)': { standby: 0.001, alarm: 0.095 },
  'Horn/Strobe (30cd)': { standby: 0.001, alarm: 0.130 },
  'Horn/Strobe (75cd)': { standby: 0.001, alarm: 0.177 },
  'Horn/Strobe (110cd)': { standby: 0.001, alarm: 0.210 },
  'Speaker (1/4W)': { standby: 0.001, alarm: 0.021 },
  'Speaker (1/2W)': { standby: 0.001, alarm: 0.042 },
  'Speaker (1W)': { standby: 0.001, alarm: 0.083 },
  'Monitor Module': { standby: 0.35, alarm: 0.35 },
  'Control Module': { standby: 0.35, alarm: 0.35 },
  'Relay Module': { standby: 0.35, alarm: 0.50 },
};

const wireResistance: Record<string, number> = {
  '18': 6.39,
  '16': 4.02,
  '14': 2.53,
  '12': 1.59,
  '10': 1.00,
  '8': 0.628,
};

const wireAmpacity: Record<string, number> = {
  '18': 7,
  '16': 10,
  '14': 15,
  '12': 20,
  '10': 30,
  '8': 40,
};

export function LoopLoadCalculatorPage() {
  const { currentProject, addCalculation } = useProjects();
  const [circuitName, setCircuitName] = useState('NAC Circuit 1');
  const [circuitType, setCircuitType] = useState<'NAC' | 'SLC'>('NAC');
  const [circuitVoltage, setCircuitVoltage] = useState('24');
  const [wireGauge, setWireGauge] = useState('14');
  const [circuitLength, setCircuitLength] = useState('500');
  const [devices, setDevices] = useState<Device[]>([]);
  const [result, setResult] = useState<CircuitResult | null>(null);
  const [showAddDevice, setShowAddDevice] = useState(false);

  const addDevice = (device: Omit<Device, 'id'>) => {
    const newDevice: Device = {
      ...device,
      id: `dev_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    };
    setDevices([...devices, newDevice]);
  };

  const removeDevice = (id: string) => {
    setDevices(devices.filter(d => d.id !== id));
  };

  const updateDevice = (id: string, updates: Partial<Device>) => {
    setDevices(devices.map(d => d.id === id ? { ...d, ...updates } : d));
  };

  const calculateCircuitLoad = () => {
    if (devices.length === 0) {
      alert('Please add at least one device to the circuit');
      return;
    }

    const voltage = parseFloat(circuitVoltage);
    const length = parseFloat(circuitLength);
    const resistance = wireResistance[wireGauge];
    const ampacity = wireAmpacity[wireGauge];

    let totalStandbyCurrent = 0;
    let totalAlarmCurrent = 0;

    devices.forEach(device => {
      totalStandbyCurrent += (device.standbyCurrentMa / 1000) * device.quantity;
      totalAlarmCurrent += (device.alarmCurrentMa / 1000) * device.quantity;
    });

    const voltageDropStandby = (2 * totalStandbyCurrent * resistance * length) / 1000;
    const voltageDropAlarm = (2 * totalAlarmCurrent * resistance * length) / 1000;
    const maxVoltageDrop = Math.max(voltageDropStandby, voltageDropAlarm);
    const voltageAtEOL = voltage - maxVoltageDrop;
    const maxLength = (3 * 1000) / (2 * totalAlarmCurrent * resistance);
    const powerSupplyRequired = totalAlarmCurrent * voltage * 1.2;

    // Battery Calculations per NFPA 72
    // 24 hours standby + 5 minutes alarm (0.0833 hours)
    const batteryCapacity24h5m = (totalStandbyCurrent * 24) + (totalAlarmCurrent * (5 / 60));
    // 24 hours standby + 15 minutes alarm (0.25 hours) for voice evacuation
    const batteryCapacity24h = (totalStandbyCurrent * 24) + (totalAlarmCurrent * 0.25);
    // Charger rating: 1.2 x standby current (per NFPA 72)
    const chargerRating = totalStandbyCurrent * 1.2;

    const messages: string[] = [];
    const nfpaReferences: string[] = [];
    let status: 'success' | 'warning' | 'error' = 'success';

    // Voltage drop validation per NFPA 72
    const minVoltageNAC = circuitType === 'NAC' ? 16.0 : 16.0; // NFPA 72 Section 10.17.1
    // Maximum voltage per NFPA 72 Section 10.17.1
    // const maxVoltageNAC = circuitType === 'NAC' ? 33.0 : 29.0;
    
    if (voltageAtEOL < minVoltageNAC) {
      status = 'error';
      messages.push(`End-of-line voltage (${voltageAtEOL.toFixed(2)}V) below minimum ${minVoltageNAC}V.`);
      nfpaReferences.push('NFPA 72-2022 Section 10.17.1 - Voltage requirements');
    } else if (voltageAtEOL < minVoltageNAC + 2) {
      if (status === 'success') status = 'warning';
      messages.push(`End-of-line voltage (${voltageAtEOL.toFixed(2)}V) is close to minimum.`);
      nfpaReferences.push('NFPA 72-2022 Section 10.17.1 - Voltage requirements');
    }

    if (maxVoltageDrop > 3.0) {
      status = 'error';
      messages.push(`Voltage drop (${maxVoltageDrop.toFixed(2)}V) exceeds 3V maximum.`);
      nfpaReferences.push('NFPA 72-2022 Section 10.17.2 - Voltage drop limitations');
    } else if (maxVoltageDrop > 2.0) {
      if (status === 'success') status = 'warning';
      messages.push(`Voltage drop (${maxVoltageDrop.toFixed(2)}V) is high. Consider larger wire.`);
    } else {
      messages.push(`Voltage drop (${maxVoltageDrop.toFixed(2)}V) is acceptable.`);
    }

    if (totalAlarmCurrent > ampacity) {
      status = 'error';
      messages.push(`Current (${totalAlarmCurrent.toFixed(2)}A) exceeds wire ampacity (${ampacity}A).`);
      nfpaReferences.push('NEC Article 760 - Fire Alarm Systems');
    }

    if (circuitType === 'NAC' && totalAlarmCurrent > 3.0) {
      if (status === 'success') status = 'warning';
      messages.push('NAC circuit current exceeds typical 3A limit. Verify panel capacity.');
      nfpaReferences.push('NFPA 72-2022 Section 10.6.7 - Notification Appliance Circuits');
    }

    if (circuitType === 'SLC' && devices.length > 127) {
      status = 'error';
      messages.push('SLC exceeds 127 device limit.');
      nfpaReferences.push('NFPA 72-2022 Section 10.6.6 - Signaling Line Circuits');
    }

    let recommendedGauge = wireGauge;
    if (maxVoltageDrop > 2.0 || totalAlarmCurrent > ampacity * 0.8) {
      const gauges = Object.keys(wireResistance);
      const currentIndex = gauges.indexOf(wireGauge);
      if (currentIndex > 0) {
        recommendedGauge = gauges[currentIndex - 1];
        messages.push(`Recommended wire gauge: ${recommendedGauge} AWG for better performance.`);
      }
    }

    const calculationResult: CircuitResult = {
      totalStandbyCurrent,
      totalAlarmCurrent,
      requiredWireGauge: recommendedGauge,
      voltageDrop: maxVoltageDrop,
      voltageAtEOL,
      maxCircuitLength: maxLength,
      powerSupplyRequired,
      batteryCapacity24h,
      batteryCapacity24h5m,
      chargerRating,
      status,
      messages,
      nfpaReferences
    };

    setResult(calculationResult);

    if (currentProject && addCalculation) {
      addCalculation({
        type: 'battery',
        name: `${circuitName} - Loop Load`,
        inputs: {
          'Circuit Type': circuitType,
          'Voltage': `${voltage}V`,
          'Wire': `${wireGauge} AWG`,
          'Length': `${length} ft`,
          'Devices': devices.length
        },
        results: {
          'Voltage Drop': `${maxVoltageDrop.toFixed(2)} V`,
          'Max Length': `${maxLength.toFixed(0)} ft`,
          'Power Required': `${powerSupplyRequired.toFixed(1)} W`,
          'Status': status.toUpperCase()
        }
      });
    }
  };

  const exportResults = () => {
    if (!result) return;
    const exportData = {
      circuitName,
      devices,
      results: result,
      timestamp: new Date().toISOString()
    };
    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${circuitName.replace(/\s+/g, '_')}_${Date.now()}.json`;
    link.click();
  };

  const exportCSV = () => {
    if (!result) return;
    
    const csvRows = [
      ['Loop Load Calculation Report'],
      ['Generated', new Date().toLocaleString()],
      [''],
      ['CIRCUIT CONFIGURATION'],
      ['Circuit Name', circuitName],
      ['Circuit Type', circuitType],
      ['Supply Voltage (V)', circuitVoltage],
      ['Wire Gauge (AWG)', wireGauge],
      ['Circuit Length (ft)', circuitLength],
      [''],
      ['DEVICES'],
      ['Name', 'Type', 'Quantity', 'Standby (mA)', 'Alarm (mA)'],
      ...devices.map(d => [d.name, d.type, d.quantity.toString(), d.standbyCurrentMa.toString(), d.alarmCurrentMa.toString()]),
      [''],
      ['CALCULATION RESULTS'],
      ['Total Standby Current (A)', result.totalStandbyCurrent.toFixed(3)],
      ['Total Alarm Current (A)', result.totalAlarmCurrent.toFixed(3)],
      ['Voltage Drop (V)', result.voltageDrop.toFixed(2)],
      ['Voltage at EOL (V)', result.voltageAtEOL.toFixed(2)],
      ['Max Circuit Length (ft)', result.maxCircuitLength.toFixed(0)],
      ['Power Supply Required (W)', result.powerSupplyRequired.toFixed(1)],
      ['Battery Capacity 24h+5min (Ah)', result.batteryCapacity24h5m.toFixed(2)],
      ['Battery Capacity 24h+15min (Ah)', result.batteryCapacity24h.toFixed(2)],
      ['Charger Rating (A)', result.chargerRating.toFixed(3)],
      ['Recommended Wire Gauge', result.requiredWireGauge + ' AWG'],
      ['Status', result.status.toUpperCase()],
      [''],
      ['MESSAGES'],
      ...result.messages.map(m => [m]),
      [''],
      ['NFPA REFERENCES'],
      ...result.nfpaReferences.map(r => [r]),
    ];
    
    const csvContent = csvRows.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${circuitName.replace(/\s+/g, '_')}_${Date.now()}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-gradient-to-r from-orange-600 to-red-600 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <Zap className="w-16 h-16 mx-auto mb-4" />
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Loop Load Calculator</h1>
            <p className="text-xl text-orange-100">
              Calculate circuit loads, wire sizing, and voltage drop
            </p>
            <div className="mt-6">
              <Link
                to="/device-database"
                className="inline-flex items-center px-6 py-3 bg-white text-orange-600 font-semibold rounded-lg hover:bg-orange-50 transition-colors duration-200"
              >
                <Database className="w-5 h-5 mr-2" />
                Browse Device Database
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">Circuit Configuration</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Circuit Name</label>
              <input
                type="text"
                value={circuitName}
                onChange={(e) => setCircuitName(e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Circuit Type</label>
              <select
                value={circuitType}
                onChange={(e) => setCircuitType(e.target.value as 'NAC' | 'SLC')}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500"
              >
                <option value="NAC">NAC</option>
                <option value="SLC">SLC</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Voltage</label>
              <select
                value={circuitVoltage}
                onChange={(e) => setCircuitVoltage(e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500"
              >
                <option value="12">12V</option>
                <option value="24">24V</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Wire Gauge</label>
              <select
                value={wireGauge}
                onChange={(e) => setWireGauge(e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500"
              >
                {Object.keys(wireResistance).map(gauge => (
                  <option key={gauge} value={gauge}>{gauge} AWG</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Length (ft)</label>
              <input
                type="number"
                value={circuitLength}
                onChange={(e) => setCircuitLength(e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={() => setShowAddDevice(true)}
                className="w-full py-3 px-4 bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-lg flex items-center justify-center"
              >
                <Plus className="w-5 h-5 mr-2" />
                Add Device
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">Devices ({devices.length})</h2>
          {devices.length === 0 ? (
            <div className="text-center py-12">
              <Zap className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 mb-6">No devices added</p>
              <button
                onClick={() => setShowAddDevice(true)}
                className="px-6 py-3 bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-lg"
              >
                <Plus className="w-5 h-5 inline mr-2" />
                Add Device
              </button>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 border-b">
                      <th className="px-4 py-3 text-left text-sm font-semibold">Device</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold">Qty</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold">Standby (mA)</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold">Alarm (mA)</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {devices.map((device) => (
                      <tr key={device.id} className="border-b hover:bg-slate-50">
                        <td className="px-4 py-3 text-sm">{device.name}</td>
                        <td className="px-4 py-3 text-center">
                          <input
                            type="number"
                            value={device.quantity}
                            onChange={(e) => updateDevice(device.id, { quantity: parseInt(e.target.value) || 1 })}
                            className="w-16 px-2 py-1 text-center border rounded"
                            min="1"
                          />
                        </td>
                        <td className="px-4 py-3 text-right text-sm">{device.standbyCurrentMa.toFixed(2)}</td>
                        <td className="px-4 py-3 text-right text-sm">{device.alarmCurrentMa.toFixed(2)}</td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => removeDevice(device.id)}
                            className="p-1 text-red-600 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                    <tr className="bg-slate-100 font-bold">
                      <td className="px-4 py-3 text-sm">TOTAL</td>
                      <td className="px-4 py-3 text-center text-sm">{devices.length}</td>
                      <td className="px-4 py-3 text-right text-sm">
                        {devices.reduce((sum, d) => sum + d.standbyCurrentMa * d.quantity, 0).toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-right text-sm">
                        {devices.reduce((sum, d) => sum + d.alarmCurrentMa * d.quantity, 0).toFixed(2)}
                      </td>
                      <td></td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="mt-6 flex justify-center">
                <button
                  onClick={calculateCircuitLoad}
                  className="px-8 py-4 bg-orange-600 hover:bg-orange-700 text-white text-lg font-bold rounded-lg flex items-center"
                >
                  <Calculator className="w-6 h-6 mr-3" />
                  Calculate
                </button>
              </div>
            </>
          )}
        </div>

        {result && (
          <div className="space-y-6">
            {/* Circuit Summary Dashboard */}
            <div className="bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-xl shadow-2xl p-8">
              <h2 className="text-3xl font-bold mb-6 flex items-center">
                <Activity className="w-8 h-8 mr-3" />
                Circuit Summary Dashboard
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div>
                  <p className="text-orange-100 text-sm mb-1">Circuit Type</p>
                  <p className="text-3xl font-bold">{circuitType}</p>
                </div>
                <div>
                  <p className="text-orange-100 text-sm mb-1">Total Devices</p>
                  <p className="text-3xl font-bold">{devices.length}</p>
                </div>
                <div>
                  <p className="text-orange-100 text-sm mb-1">Wire Gauge</p>
                  <p className="text-3xl font-bold">{wireGauge} AWG</p>
                </div>
                <div>
                  <p className="text-orange-100 text-sm mb-1">Circuit Length</p>
                  <p className="text-3xl font-bold">{circuitLength} ft</p>
                </div>
              </div>
            </div>

            {/* Status Messages */}
            <div className={`rounded-xl shadow-lg p-6 ${
              result.status === 'success' ? 'bg-green-50 border-2 border-green-200' :
              result.status === 'warning' ? 'bg-yellow-50 border-2 border-yellow-200' :
              'bg-red-50 border-2 border-red-200'
            }`}>
              <div className="flex items-start">
                {result.status === 'success' ? (
                  <CheckCircle2 className="w-8 h-8 text-green-600 mr-3" />
                ) : result.status === 'warning' ? (
                  <AlertCircle className="w-8 h-8 text-yellow-600 mr-3" />
                ) : (
                  <AlertCircle className="w-8 h-8 text-red-600 mr-3" />
                )}
                <div className="flex-1">
                  <h3 className="text-xl font-bold mb-2">
                    {result.status === 'success' ? 'Circuit Design Valid ✓' : 
                     result.status === 'warning' ? 'Circuit Needs Review ⚠' : 
                     'Circuit Design Error ✗'}
                  </h3>
                  <ul className="space-y-1">
                    {result.messages.map((msg, idx) => (
                      <li key={idx} className="text-sm">• {msg}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Current & Voltage Metrics */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-bold mb-4 flex items-center">
                <Zap className="w-6 h-6 mr-2 text-orange-600" />
                Current & Voltage Analysis
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-sm text-blue-600 mb-1">Standby Current</p>
                  <p className="text-2xl font-bold text-blue-900">{(result.totalStandbyCurrent * 1000).toFixed(2)} <span className="text-sm">mA</span></p>
                  <p className="text-xs text-blue-600 mt-1">{result.totalStandbyCurrent.toFixed(3)} A</p>
                </div>

                <div className="bg-orange-50 rounded-lg p-4">
                  <p className="text-sm text-orange-600 mb-1">Alarm Current</p>
                  <p className="text-2xl font-bold text-orange-900">{(result.totalAlarmCurrent * 1000).toFixed(2)} <span className="text-sm">mA</span></p>
                  <p className="text-xs text-orange-600 mt-1">{result.totalAlarmCurrent.toFixed(3)} A</p>
                </div>

                <div className={`rounded-lg p-4 ${result.voltageDrop > 2 ? 'bg-red-50' : 'bg-green-50'}`}>
                  <p className={`text-sm mb-1 ${result.voltageDrop > 2 ? 'text-red-600' : 'text-green-600'}`}>Voltage Drop</p>
                  <p className={`text-2xl font-bold ${result.voltageDrop > 2 ? 'text-red-900' : 'text-green-900'}`}>{result.voltageDrop.toFixed(2)} <span className="text-sm">V</span></p>
                  <p className={`text-xs mt-1 ${result.voltageDrop > 2 ? 'text-red-600' : 'text-green-600'}`}>{((result.voltageDrop / parseFloat(circuitVoltage)) * 100).toFixed(1)}% drop</p>
                </div>

                <div className={`rounded-lg p-4 ${result.voltageAtEOL < 18 ? 'bg-red-50' : 'bg-green-50'}`}>
                  <p className={`text-sm mb-1 ${result.voltageAtEOL < 18 ? 'text-red-600' : 'text-green-600'}`}>End-of-Line Voltage</p>
                  <p className={`text-2xl font-bold ${result.voltageAtEOL < 18 ? 'text-red-900' : 'text-green-900'}`}>{result.voltageAtEOL.toFixed(2)} <span className="text-sm">V</span></p>
                  <p className={`text-xs mt-1 ${result.voltageAtEOL < 18 ? 'text-red-600' : 'text-green-600'}`}>Min: 16.0V</p>
                </div>
              </div>
            </div>

            {/* Battery Calculations */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-bold mb-4 flex items-center">
                <Battery className="w-6 h-6 mr-2 text-green-600" />
                Battery Backup Calculations
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-6 border-2 border-green-200">
                  <p className="text-sm text-green-700 font-semibold mb-2">24h Standby + 5min Alarm</p>
                  <p className="text-4xl font-bold text-green-900">{result.batteryCapacity24h5m.toFixed(2)} <span className="text-xl">Ah</span></p>
                  <p className="text-xs text-green-600 mt-2">Per NFPA 72 Section 10.6.7.2.1</p>
                  <p className="text-xs text-green-700 mt-1">Standard fire alarm systems</p>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-lg p-6 border-2 border-purple-200">
                  <p className="text-sm text-purple-700 font-semibold mb-2">24h Standby + 15min Alarm</p>
                  <p className="text-4xl font-bold text-purple-900">{result.batteryCapacity24h.toFixed(2)} <span className="text-xl">Ah</span></p>
                  <p className="text-xs text-purple-600 mt-2">Per NFPA 72 Section 10.6.7.2.2</p>
                  <p className="text-xs text-purple-700 mt-1">Voice evacuation systems</p>
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg p-6 border-2 border-blue-200">
                  <p className="text-sm text-blue-700 font-semibold mb-2">Charger Rating</p>
                  <p className="text-4xl font-bold text-blue-900">{result.chargerRating.toFixed(3)} <span className="text-xl">A</span></p>
                  <p className="text-xs text-blue-600 mt-2">1.2× standby current</p>
                  <p className="text-xs text-blue-700 mt-1">Minimum charger capacity</p>
                </div>
              </div>
            </div>

            {/* Wire & Circuit Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-bold mb-4 flex items-center">
                  <Cable className="w-5 h-5 mr-2 text-indigo-600" />
                  Wire Specifications
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-slate-600">Current Wire Gauge</span>
                    <span className="font-bold text-slate-900">{wireGauge} AWG</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-slate-600">Recommended Gauge</span>
                    <span className={`font-bold ${result.requiredWireGauge !== wireGauge ? 'text-orange-600' : 'text-green-600'}`}>
                      {result.requiredWireGauge} AWG
                    </span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-slate-600">Wire Ampacity</span>
                    <span className="font-bold text-slate-900">{wireAmpacity[wireGauge]} A</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-slate-600">Wire Resistance</span>
                    <span className="font-bold text-slate-900">{wireResistance[wireGauge]} Ω/1000ft</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-slate-600">Max Circuit Length</span>
                    <span className="font-bold text-slate-900">{result.maxCircuitLength.toFixed(0)} ft</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-bold mb-4 flex items-center">
                  <Info className="w-5 h-5 mr-2 text-purple-600" />
                  Power Supply Requirements
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-slate-600">Power Required</span>
                    <span className="font-bold text-slate-900">{result.powerSupplyRequired.toFixed(1)} W</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-slate-600">Circuit Voltage</span>
                    <span className="font-bold text-slate-900">{circuitVoltage} VDC</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-slate-600">Utilization</span>
                    <span className="font-bold text-slate-900">
                      {((result.totalAlarmCurrent / wireAmpacity[wireGauge]) * 100).toFixed(1)}%
                    </span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-slate-600">Safety Factor</span>
                    <span className="font-bold text-green-600">1.2× (20%)</span>
                  </div>
                </div>
              </div>
            </div>

            {/* NFPA 72 Code References */}
            {result.nfpaReferences.length > 0 && (
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl shadow-lg p-6 border-2 border-blue-200">
                <h3 className="text-xl font-bold mb-4 flex items-center text-blue-900">
                  <BookOpen className="w-6 h-6 mr-2" />
                  NFPA 72 Code References
                </h3>
                <div className="space-y-2">
                  {result.nfpaReferences.map((ref, idx) => (
                    <div key={idx} className="flex items-start bg-white rounded-lg p-3 border border-blue-200">
                      <CheckCircle2 className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-slate-700">{ref}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t border-blue-200">
                  <p className="text-xs text-blue-700">
                    <strong>Note:</strong> All calculations and recommendations are based on NFPA 72-2022 National Fire Alarm and Signaling Code. 
                    Always verify with local Authority Having Jurisdiction (AHJ) for specific requirements.
                  </p>
                </div>
              </div>
            )}

            <div className="flex justify-center gap-4">
              <button
                onClick={exportCSV}
                className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg flex items-center"
              >
                <Download className="w-5 h-5 mr-2" />
                Export CSV
              </button>
              <button
                onClick={exportResults}
                className="px-6 py-3 bg-slate-600 hover:bg-slate-700 text-white font-semibold rounded-lg flex items-center"
              >
                <Download className="w-5 h-5 mr-2" />
                Export JSON
              </button>
            </div>
          </div>
        )}

        {showAddDevice && (
          <AddDeviceModal
            onClose={() => setShowAddDevice(false)}
            onAdd={(device) => {
              addDevice(device);
              setShowAddDevice(false);
            }}
            presets={devicePresets}
          />
        )}
      </div>
    </div>
  );
}

function AddDeviceModal({ onClose, onAdd, presets }: {
  onClose: () => void;
  onAdd: (device: Omit<Device, 'id'>) => void;
  presets: Record<string, { standby: number; alarm: number }>;
}) {
  const [selectedPreset, setSelectedPreset] = useState('');
  const [selectedDbDevice, setSelectedDbDevice] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    type: 'horn_strobe' as Device['type'],
    quantity: '1',
    standbyCurrentMa: '',
    alarmCurrentMa: ''
  });

  const handlePresetChange = (preset: string) => {
    setSelectedPreset(preset);
    setSelectedDbDevice('');
    if (preset && presets[preset]) {
      setFormData(prev => ({
        ...prev,
        name: preset,
        standbyCurrentMa: (presets[preset].standby * 1000).toString(),
        alarmCurrentMa: (presets[preset].alarm * 1000).toString()
      }));
    }
  };

  const handleDbDeviceChange = (deviceId: string) => {
    setSelectedDbDevice(deviceId);
    setSelectedPreset('');
    const device = deviceDatabase.find(d => d.id === deviceId);
    if (device) {
      setFormData(prev => ({
        ...prev,
        name: `${device.manufacturer} ${device.model}`,
        standbyCurrentMa: device.standbyCurrentMa.toString(),
        alarmCurrentMa: device.alarmCurrentMa.toString()
      }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onAdd({
      name: formData.name,
      type: formData.type,
      quantity: parseInt(formData.quantity) || 1,
      standbyCurrentMa: parseFloat(formData.standbyCurrentMa) || 0,
      alarmCurrentMa: parseFloat(formData.alarmCurrentMa) || 0
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
        <div className="p-6 border-b">
          <h3 className="text-xl font-bold">Add Device</h3>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2 flex items-center">
              <Database className="w-4 h-4 mr-2" />
              Device Database
            </label>
            <select
              value={selectedDbDevice}
              onChange={(e) => handleDbDeviceChange(e.target.value)}
              className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500"
            >
              <option value="">Select from database...</option>
              {deviceDatabase.map(device => (
                <option key={device.id} value={device.id}>
                  {device.manufacturer} {device.model} - {device.description}
                </option>
              ))}
            </select>
          </div>
          <div className="text-center text-sm text-slate-500">OR</div>
          <div>
            <label className="block text-sm font-medium mb-2">Quick Preset</label>
            <select
              value={selectedPreset}
              onChange={(e) => handlePresetChange(e.target.value)}
              className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500"
            >
              <option value="">Custom Device</option>
              {Object.keys(presets).map(preset => (
                <option key={preset} value={preset}>{preset}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Device Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              required
              className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Quantity</label>
            <input
              type="number"
              value={formData.quantity}
              onChange={(e) => setFormData(prev => ({ ...prev, quantity: e.target.value }))}
              min="1"
              required
              className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Standby Current (mA)</label>
            <input
              type="number"
              step="0.001"
              value={formData.standbyCurrentMa}
              onChange={(e) => setFormData(prev => ({ ...prev, standbyCurrentMa: e.target.value }))}
              required
              className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Alarm Current (mA)</label>
            <input
              type="number"
              step="0.001"
              value={formData.alarmCurrentMa}
              onChange={(e) => setFormData(prev => ({ ...prev, alarmCurrentMa: e.target.value }))}
              required
              className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500"
            />
          </div>
        </form>
        <div className="p-6 border-t flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2 px-4 bg-slate-100 hover:bg-slate-200 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="flex-1 py-2 px-4 bg-orange-600 hover:bg-orange-700 text-white rounded-lg"
          >
            Add Device
          </button>
        </div>
      </div>
    </div>
  );
}
